// srt-load.js

import { updateAllPayloads } from './srt-tare-gross.js';
import {
   getDefaultAxleWidth,
   getDefaultAxleHeight,
   getDefaultAxleMass,
   getEstimatedSprungTareCg,
} from '../../common/srt/srt-common-axle-defaults.js';

const defaults = {
   standard: {
      'load-bed-height': 1.4, // m
      'load-height-max': 3.8, // m
   },
   sloping: {
      'load-height-front': 3.7,
      'load-height-rear': 3.2,
      'load-bed-height-front': 1.45,
      'load-bed-height-rear': 1.2,
   },
   step: {
      'deck1-height': 1.45,
      'deck2-height': 0.95,
      'deck-load-height-max': 3.8, // added
      'deck1-length': 5.0,
      'deck2-length': 8.0,
   },
};
const defaultCogHeight = 2.36;

//
// Axle Load Section logic and Payload Geometry Section
//

export function initLoad(unitIndex = 0) {
   // Axle Load Section setup
   bindAxleLoadAdvVis(unitIndex);
   resetLoadFields(unitIndex); // Reset all load fields for the unit
   // new single handler for all axle load fields
   bindAxleLoadHandlers(unitIndex);

   // Payload Geometry Section setup
   bindLoadCategoryVisibility(unitIndex);
   bindDeckStyleVisibility(unitIndex);
}

function resetLoadFields(unitIndex = 0) {
   hideAdvancedFields(unitIndex);
   updateCalcLoadFields(unitIndex); // Reset all calculated fields
}

// Update the load section for the specified vehicle type and unit index (when user changes vehicle) and page load
export function updateLoadOptions(vt, unitIndex) {
   resetLoadFields(unitIndex); // Reset all load fields for the unit
   resetPayloadGeometry(unitIndex); // Reset payload geometry fields
}

//
// Axle Load Section
//

function bindAxleLoadAdvVis(unitIndex) {
   const $toggle = $(`#axle-advanced-toggle-${unitIndex}`);
   const $section = $(`#collapse-axleload-${unitIndex}`);

   function updateAxleLoadAdvVisibility() {
      const show = $toggle.is(':checked');
      $section.find('.advanced-only').toggle(show);
   }

   // On initial load
   updateAxleLoadAdvVisibility();

   // On change
   $toggle.on('change', updateAxleLoadAdvVisibility);
}

// Clear and hide the advanced and optional unsprung Axle Load fields
function hideAdvancedFields(unitIndex) {
   // Clear advanced toggle switch (uncheck it)
   const $toggle = $(`#axle-advanced-toggle-${unitIndex}`);
   $toggle.prop('checked', false);

   // Hide the advanced-only section if visible
   const $section = $(`#collapse-axleload-${unitIndex}`);
   $section.find('.advanced-only').hide();
}

export function bindAxleLoadHandlers(unitIdx = 0) {
   const selectors = [
      `#f-gross-mass-${unitIdx}`,
      `#f-tare-mass-${unitIdx}`,
      `#r-gross-mass-${unitIdx}`,
      `#r-tare-mass-${unitIdx}`,
      `#f-tyre-size-${unitIdx}`,
      `#r-tyre-size-${unitIdx}`,
      `#f-tyre-config-${unitIdx}`,
      `#r-tyre-config-${unitIdx}`,
      `#f-unsprung-mass-${unitIdx}`,
      `#r-unsprung-mass-${unitIdx}`,
   ].join(', ');

   // Small self-contained debounce utility
   let timer;
   const debounce = (fn, delay = 300) => {
      clearTimeout(timer);
      timer = setTimeout(fn, delay);
   };

   $(selectors).on('input change', function () {
      const id = this.id;

      debounce(() => {
         // Update payloads if a mass field changed
         if (id.includes('gross-mass') || id.includes('tare-mass')) {
            updateAllPayloads(unitIdx);
         }

         // Update tyre-dependent defaults if tyre size/config changed
         if (id.includes('tyre')) {
            if (id.startsWith('f-')) {
               updateDefaultAdvancedFields('f', unitIdx);
            } else if (id.startsWith('r-')) {
               updateDefaultAdvancedFields('r', unitIdx);
            }
         }

         if (id.includes('unsprung-mass') || id.includes('tare-mass') || id.includes('tyre')) {
            updateSprungTareMass(unitIdx);
         }

         if (id.includes('unsprung-mass') || id.includes('tyre-size')) {
            // Estimates the sprung CG height based on the current values, can be overridden by user input
            EstSprungCog(unitIdx);
         }
      }, 400); // Adjust delay as needed
   });
}

function updateCalcLoadFields(unitIdx) {
   // Update all of the calculated load fields based on the current values
   updateAllPayloads(unitIdx); // calculated payloads based on gross and tare
   updateDefaultAdvancedFields('f', unitIdx); // unsprung mass, unsprung tare CoG, contact width and total sprung tare mass
   updateDefaultAdvancedFields('r', unitIdx);
   updateSprungTareMass(unitIdx); // total sprung tare mass based on unsprung and tare mass
   EstSprungCog(unitIdx); // set the estimated sprung CG height placeholder
}
export function updateDefaultAdvancedFields(group, unitIdx) {
   // Set default values for advanced  axle fields based when tyre size and config change
   const isFront = group.startsWith('f');
   const isTruck = $(`#vehicle-type-${unitIdx}`).val() === 'truck';
   let axleTypeCode = 3;
   if (isTruck) {
      axleTypeCode = isFront ? 1 : 2;
   }

   const tyreSize = tyreStringToIndex($(`#${group}-tyre-size-${unitIdx}`).val());
   const tyreConfig = tyreConfigToIndex($(`#${group}-tyre-config-${unitIdx}`).val());
   const unsprungMass = getDefaultAxleMass(tyreSize, tyreConfig, axleTypeCode);
   const unsprungCgHeight = getDefaultAxleHeight(tyreSize);

   $(`#${group}-unsprung-mass-${unitIdx}`).val(unsprungMass).removeClass('is-invalid');
   $(`#${group}-unsprung-cg-height-${unitIdx}`).val(unsprungCgHeight).removeClass('is-invalid');

   const maxContactWidth = getDefaultAxleWidth();
   $(`#${group}-max-contact-width-${unitIdx}`).val(maxContactWidth).removeClass('is-invalid');
}

function tyreConfigToIndex(config) {
   // MRWA uses - 0=single, 1=wide single, 2=dual, 3=row of eight
   const map = { Single: 0, 'Wide Single': 1, Dual: 2, 'Row of Eight': 3 };
   return map[config] !== undefined ? map[config] : -1;
}
// Now we populate defaults when the tyre size or config changes
function tyreStringToIndex(str) {
   const map = { 17.5: 0, 19.5: 1, 22.5: 2 };
   return map[str] !== undefined ? map[str] : -1;
}

function getUnsprungMassAxles(unitIndex, position) {
   // Get the total unsprung mass for the specified position (front or rear)
   const axles = $(`#${position}-axles-${unitIndex}`).val();
   const pos = position === 'front' ? 'f' : 'r';
   const $el = $(`#${pos}-unsprung-mass-${unitIndex}`);
   const unsprungMass = parseFloat($el.val()) || 0;
   return axles * unsprungMass;
}
// Auto calculate the total sprung tare mass field based on the inputs
export function updateSprungTareMass(unitIndex) {
   function val(id, def = '') {
      const $el = $('#' + id);
      if (!$el.length) return def;
      if ($el.is('select')) return $el.find('option:selected').text() || def;
      return $el.val() || def;
   }

   // Update the total sprung tare mass based on the per axle unsprung mass
   const fTare = parseFloat(val(`f-tare-mass-${unitIndex}`));
   const fUnsprungTotal = getUnsprungMassAxles(unitIndex, 'front');
   const $fOutput = $(`#f-unsprung-mass-total-${unitIndex}`);
   $fOutput.val(Math.round(fUnsprungTotal));

   const rTare = parseFloat(val(`r-tare-mass-${unitIndex}`));
   const rUnsprungTotal = getUnsprungMassAxles(unitIndex, 'rear');
   const $rOutput = $(`#r-unsprung-mass-total-${unitIndex}`);
   $rOutput.val(Math.round(rUnsprungTotal));

   // Calculate the total sprung tare mass (multiple axles)
   const $output = $(`#sprung-tare-mass-${unitIndex}`);
   if (!isNaN(fTare) && !isNaN(rTare) && !isNaN(fUnsprungTotal) && !isNaN(rUnsprungTotal)) {
      const sprungTare = fTare + rTare - fUnsprungTotal - rUnsprungTotal;

      if (sprungTare < 0) {
         $output
            .val('')
            .addClass('is-invalid')
            .attr('data-bs-toggle', 'tooltip')
            .attr('data-bs-placement', 'top')
            .attr('title', 'Sprung tare mass is negative — check tare and unsprung values.');
         bootstrap.Tooltip.getOrCreateInstance($output[0]).enable();
      } else {
         $output
            .val(Math.round(sprungTare))
            .removeClass('is-invalid')
            .removeAttr('data-bs-toggle')
            .removeAttr('data-bs-placement')
            .removeAttr('title');

         const existingTooltip = bootstrap.Tooltip.getInstance($output[0]);
         if (existingTooltip) existingTooltip.dispose();
      }
   } else {
      $output
         .val('')
         .removeClass('is-invalid')
         .removeAttr('data-bs-toggle')
         .removeAttr('data-bs-placement')
         .removeAttr('title');

      const existingTooltip = bootstrap.Tooltip.getInstance($output[0]);
      if (existingTooltip) existingTooltip.dispose();
   }
}

function EstSprungCog(unitIndex) {
   // Provide a calculated estimate for the sprung tare mass CoG based on defaults

   const vt = $(`#vehicle-type-${unitIndex}`).val();
   const tyreSizeF = tyreStringToIndex($(`#f-tyre-size-${unitIndex}`).val());
   const tyreSizeR = tyreStringToIndex($(`#r-tyre-size-${unitIndex}`).val());
   // const tyreConfigF = tyreConfigToIndex($(`#f-tyre-config-${unitIdx}`).val());
   // const tyreConfigR = tyreConfigToIndex($(`#r-tyre-config-${unitIdx}`).val());
   const frontAxleMass = getUnsprungMassAxles(unitIndex, 'front');
   const rearAxleMass = getUnsprungMassAxles(unitIndex, 'rear');

   const val = getEstimatedSprungTareCg(vt, tyreSizeF, tyreSizeR, frontAxleMass, rearAxleMass);

   const $output = $(`#sprung-cg-height-${unitIndex}`);
   if (Number.isFinite(val)) {
      $output.val(val);
   } else {
      console.warn('Invalid estimated sprung CG height:', val);
   }
}

//
// Payload Geometry Section
//

function resetPayloadGeometry(unitIndex = 0) {
   // Reset the payload geometry fields to their defaults

   // reset the deck style
   $(`#deck-style-${unitIndex}`).val('standard').trigger('change');

   // reset the load category
   $(`#load-category-${unitIndex}`).val('mixed-freight').trigger('change');
}

// Bind visibility of the Payload Distribution dropdown based on selection
// This controls whether the custom payload CG height field is shown or hidden
// It also enables/disables the standard height inputs based on the selection
// The "Custom" option shows the Payload CG height field, while "Standard" and "Mixed Freight" hides
function bindLoadCategoryVisibility(unitIndex) {
   // Payload Distribution section
   const $category = $(`#load-category-${unitIndex}`);
   const $cgWrapper = $(`#payload-cg-height-wrapper-${unitIndex}`);
   const $standardFields = $(`#standard-fields-${unitIndex} input`);

   function updateVisibility(value) {
      const isOther = value === 'other';
      // Only visible when "Custom" is selected instead of "Standard" or "Mixed Freight"
      if (isOther) {
         $cgWrapper.show();
      } else {
         $cgWrapper.hide();
      }

      // Enable or disable standard height inputs - Load Bed Height from Ground and load Maximum Height from Ground
      // when the Custom option is selected
      $standardFields.prop('disabled', isOther);

      // also clear the payload CG height field when switching away from Custom
      const $input = $(`#payload-cg-height-${unitIndex}`);
      if (!isOther) {
         $input.val('');
      } else {
         // If switching to Custom, set the default value
         $input.val(defaultCogHeight);
      }
   }

   // Initial call with current value
   updateVisibility($category.val());

   // On change
   $category.on('change', function () {
      updateVisibility(this.value);
   });
}

// Bind visibility of the Deck Style dropdown based on selection - Standard, Sloping, or Step
// The "Custom" option for Payload Distribution is hidden for sloping
// and step styles to prevent invalid selections.
// If the current selection becomes invalid, it resets to "Mixed Freight".
function bindDeckStyleVisibility(unitIndex) {
   const $deck = $(`#deck-style-${unitIndex}`);
   const $shared = $(`#shared-fields-${unitIndex}`);
   const $standard = $(`#standard-fields-${unitIndex}`);
   const $sloping = $(`#sloping-fields-${unitIndex}`);
   const $step = $(`#step-fields-${unitIndex}`);
   const $loadCategory = $(`#load-category-${unitIndex}`);
   const $customOption = $loadCategory.find('option[value="other"]');

   function updateDeckVisibility(value) {
      // Show/hide field sections
      $standard.toggle(value === 'standard');
      $sloping.toggle(value === 'sloping');
      $step.toggle(value === 'step');

      const hideCustom = value === 'sloping' || value === 'step';

      // If currently selected and now invalid, reset it
      if (hideCustom && $loadCategory.val() === 'other') {
         $loadCategory.val('mixed-freight').trigger('change');
      }

      // Hide or show the <option>
      $customOption.toggle(!hideCustom);

      // Apply realistic defaults based on deck style
      applyDeckDefaults(value, unitIndex);
   }

   // Initial setup
   updateDeckVisibility($deck.val());

   // On change of deck style
   $deck.on('change', function () {
      updateDeckVisibility(this.value);
   });
}

function applyDeckDefaults(deckStyle, unitIndex = 0) {
   const setIfEmpty = (fieldId, value) => {
      const $input = $(`#${fieldId}-${unitIndex}`);
      if ($input.val().trim() === '') {
         $input.val(value).trigger('change');
      }
   };

   const config = defaults[deckStyle];
   if (!config) {
      console.warn(`Unknown deck style: ${deckStyle}`);
      return;
   }

   // Apply each field if empty
   for (const [field, value] of Object.entries(config)) {
      setIfEmpty(field, value);
   }
}
